/**
 * Scoring for the destination layer.
 *
 * Two different problems get conflated as "fuzzy search", and this file solves
 * both, because either one alone leaves the headline case broken:
 *
 * 1. SYNONYMS. Typing "time off" must find Document Hub's page labelled "PTO".
 *    No amount of edit-distance gets you from "time off" to "PTO" — they share
 *    no letters. That is what an entry's `keywords` are for.
 * 2. TYPOS AND PARTIALS. "complience" must still find "Compliance", "evals"
 *    must find "Evaluations". That is what `fuzzyScore` is for.
 */

/** Lowercase, strip punctuation, collapse whitespace. */
export function normalize(text: string): string {
  return text
    .toLowerCase()
    .replace(/[^a-z0-9\s]/g, ' ')
    .replace(/\s+/g, ' ')
    .trim();
}

export function tokenize(text: string): string[] {
  const n = normalize(text);
  return n ? n.split(' ') : [];
}

/**
 * Levenshtein distance, bailing out once it cannot beat `max`. Callers only
 * ever care about "within one or two edits", so the full matrix is wasted work.
 */
export function editDistance(a: string, b: string, max = 2): number {
  if (a === b) return 0;
  if (Math.abs(a.length - b.length) > max) return max + 1;

  let prev = Array.from({ length: b.length + 1 }, (_, i) => i);
  for (let i = 1; i <= a.length; i++) {
    const curr = [i];
    let rowMin = i;
    for (let j = 1; j <= b.length; j++) {
      const cost = a[i - 1] === b[j - 1] ? 0 : 1;
      curr[j] = Math.min(prev[j] + 1, curr[j - 1] + 1, prev[j - 1] + cost);
      if (curr[j] < rowMin) rowMin = curr[j];
    }
    // Every remaining row can only add to the minimum, so this is a safe bail.
    if (rowMin > max) return max + 1;
    prev = curr;
  }
  return prev[b.length];
}

/** Do `needle`'s tokens appear consecutively inside `haystack`'s tokens? */
export function containsSequence(haystack: string[], needle: string[]): boolean {
  if (!needle.length || needle.length > haystack.length) return false;
  for (let i = 0; i <= haystack.length - needle.length; i++) {
    if (needle.every((t, j) => haystack[i + j] === t)) return true;
  }
  return false;
}

/** Does every character of `query` appear in `text`, in order? ("evl" in "evaluations") */
export function isSubsequence(query: string, text: string): boolean {
  if (!query) return true;
  let qi = 0;
  for (let i = 0; i < text.length && qi < query.length; i++) {
    if (text[i] === query[qi]) qi++;
  }
  return qi === query.length;
}

/**
 * How well `query` matches a single field, 0-100. Tiers are deliberately far
 * apart so a weak match on a strong field never outranks a strong match on a
 * weak one once the caller applies its field weight.
 */
export function fuzzyScore(query: string, field: string): number {
  const q = normalize(query);
  const f = normalize(field);
  if (!q || !f) return 0;

  if (f === q) return 100;
  if (f.startsWith(q)) return 85;
  if (f.includes(q)) return 70;

  const qTokens = tokenize(q);
  const fTokens = tokenize(f);

  // The field is a fragment of the QUERY, not the other way round: "cancel my
  // plan" should still reach a page whose keyword is just "cancel". Matched on
  // whole words, because a raw substring test would let "equipment" hit "pip".
  if (containsSequence(qTokens, fTokens)) return 60;

  // Every query word matches some field word by prefix — "staff sched" -> "Staff Schedule".
  const allPrefix = qTokens.every((qt) => fTokens.some((ft) => ft.startsWith(qt)));
  if (allPrefix) return 65;

  // Typo tolerance, per word. Short words get no slack: at <=3 characters a
  // single edit can turn one real word into a different real word.
  const allClose = qTokens.every((qt) =>
    fTokens.some((ft) => {
      const budget = qt.length <= 3 ? 0 : qt.length <= 5 ? 1 : 2;
      return budget > 0 && editDistance(qt, ft, budget) <= budget;
    }),
  );
  if (allClose) return 55;

  // Last resort: heavy abbreviations ("dailyag" -> "Daily Agenda"). Both bounds
  // matter more than they look. A short query is a subsequence of half the app
  // ("pto" sits inside "subscription"), and a long field is a subsequence sponge
  // ("ratios" scatters across "upload transactions"). An abbreviation is short
  // for something, not short for anything.
  const qc = q.replace(/ /g, '');
  const fc = f.replace(/ /g, '');
  if (qc.length >= 5 && fc.length <= qc.length * 2 && isSubsequence(qc, fc)) return 35;

  return 0;
}

/** Relative worth of a hit, by which field it landed on. */
const FIELD_WEIGHT = {
  /** The visible label — what the user is most likely aiming at. */
  label: 1,
  /**
   * Curated synonyms. Just under `label` so an entry that literally IS "PTO"
   * outranks one that merely lists "pto" as a keyword, while still letting
   * "time off" find the PTO page at all.
   */
  keyword: 0.92,
  /** The hub's own name — "finance" should surface Finance Hub's pages. */
  hub: 0.55,
} as const;

export interface Matchable {
  label: string;
  keywords?: string[];
  hubName?: string;
}

/**
 * Best score across an entry's fields, 0-100. Zero means "do not show this" —
 * a search that offers everything is the same as offering nothing.
 */
export function scoreEntry(query: string, entry: Matchable): number {
  let best = fuzzyScore(query, entry.label) * FIELD_WEIGHT.label;

  for (const kw of entry.keywords ?? []) {
    const s = fuzzyScore(query, kw) * FIELD_WEIGHT.keyword;
    if (s > best) best = s;
  }

  if (entry.hubName) {
    const s = fuzzyScore(query, entry.hubName) * FIELD_WEIGHT.hub;
    if (s > best) best = s;
  }

  return Math.round(best);
}
